/**
 * @fileOverview Rectangle
 */
import * as React from 'react';
import { SVGProps, useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { AnimationDuration, EasingInput } from '../util/types';
import { resolveDefaultProps } from '../util/resolveDefaultProps';
import { JavascriptAnimate } from '../animation/JavascriptAnimate';
import { useAnimationId } from '../util/useAnimationId';
import { interpolate } from '../util/DataUtils';
import { getTransitionVal } from '../animation/util';
import { svgPropertiesAndEvents } from '../util/svgPropertiesAndEvents';
import { roundTemplateLiteral } from '../util/round';

const getTrapezoidPath = (x: number, y: number, upperWidth: number, lowerWidth: number, height: number): string => {
  const widthGap = upperWidth - lowerWidth;
  let path;
  path = roundTemplateLiteral`M ${x},${y}`;
  path += roundTemplateLiteral`L ${x + upperWidth},${y}`;
  path += roundTemplateLiteral`L ${x + upperWidth - widthGap / 2},${y + height}`;
  path += roundTemplateLiteral`L ${x + upperWidth - widthGap / 2 - lowerWidth},${y + height}`;
  path += roundTemplateLiteral`L ${x},${y} Z`;
  return path;
};

interface TrapezoidProps {
  className?: string;
  /**
   * The x-coordinate of top left point of the trapezoid.
   * @default 0
   */
  x?: number;
  /**
   * The y-coordinate of top left point of the trapezoid.
   * @default 0
   */
  y?: number;
  /**
   * Width of the upper horizontal side of the trapezoid in pixels.
   * @default 0
   */
  upperWidth?: number;
  /**
   * Width of the lower horizontal side of the trapezoid in pixels.
   * @default 0
   */
  lowerWidth?: number;
  /**
   * Height of the trapezoid in pixels.
   * @default 0
   */
  height?: number;

  /**
   * If set to true, trapezoid will update and render with a gradual fade-in animation from left to right.
   * @default false
   */
  isUpdateAnimationActive?: boolean;
  animationBegin?: number;
  animationDuration?: AnimationDuration;
  animationEasing?: EasingInput;

  /**
   * The customized event handler of click on the trapezoid
   */
  onClick?: (e: React.MouseEvent<SVGPathElement>) => void;
  /**
   * The customized event handler of mousedown on the trapezoid
   */
  onMouseDown?: (e: React.MouseEvent<SVGPathElement>) => void;
  /**
   * The customized event handler of mouseup on the trapezoid
   */
  onMouseUp?: (e: React.MouseEvent<SVGPathElement>) => void;
  /**
   * The customized event handler of mousemove on the trapezoid
   */
  onMouseMove?: (e: React.MouseEvent<SVGPathElement>) => void;
  /**
   * The customized event handler of mouseover on the trapezoid
   */
  onMouseOver?: (e: React.MouseEvent<SVGPathElement>) => void;
  /**
   * The customized event handler of mouseout on the trapezoid
   */
  onMouseOut?: (e: React.MouseEvent<SVGPathElement>) => void;
  /**
   * The customized event handler of mouseenter on the trapezoid
   */
  onMouseEnter?: (e: React.MouseEvent<SVGPathElement>) => void;
  /**
   * The customized event handler of mouseleave on the trapezoid
   */
  onMouseLeave?: (e: React.MouseEvent<SVGPathElement>) => void;
}

export type Props = SVGProps<SVGPathElement> & TrapezoidProps;

export const defaultTrapezoidProps = {
  x: 0,
  y: 0,
  upperWidth: 0,
  lowerWidth: 0,
  height: 0,
  isUpdateAnimationActive: false,
  animationBegin: 0,
  animationDuration: 1500,
  animationEasing: 'ease',
} as const satisfies Partial<Props>;

export const Trapezoid: React.FC<Props> = outsideProps => {
  const trapezoidProps = resolveDefaultProps(outsideProps, defaultTrapezoidProps);

  const { x, y, upperWidth, lowerWidth, height, className } = trapezoidProps;
  const { animationEasing, animationDuration, animationBegin, isUpdateAnimationActive } = trapezoidProps;

  const pathRef = useRef<SVGPathElement | null>(null);
  const [totalLength, setTotalLength] = useState(-1);

  const prevUpperWidthRef = useRef<number>(upperWidth);
  const prevLowerWidthRef = useRef<number>(lowerWidth);
  const prevHeightRef = useRef<number>(height);
  const prevXRef = useRef<number>(x);
  const prevYRef = useRef<number>(y);
  const animationId = useAnimationId(outsideProps, 'trapezoid-');

  useEffect(() => {
    if (pathRef.current && pathRef.current.getTotalLength) {
      try {
        const pathTotalLength = pathRef.current.getTotalLength();

        if (pathTotalLength) {
          setTotalLength(pathTotalLength);
        }
      } catch {
        // calculate total length error
      }
    }
  }, []);

  if (
    x !== +x ||
    y !== +y ||
    upperWidth !== +upperWidth ||
    lowerWidth !== +lowerWidth ||
    height !== +height ||
    (upperWidth === 0 && lowerWidth === 0) ||
    height === 0
  ) {
    return null;
  }

  const layerClass = clsx('recharts-trapezoid', className);

  if (!isUpdateAnimationActive) {
    return (
      <g>
        <path
          {...svgPropertiesAndEvents(trapezoidProps)}
          className={layerClass}
          d={getTrapezoidPath(x, y, upperWidth, lowerWidth, height)}
        />
      </g>
    );
  }

  const prevUpperWidth = prevUpperWidthRef.current;
  const prevLowerWidth = prevLowerWidthRef.current;
  const prevHeight = prevHeightRef.current;
  const prevX = prevXRef.current;
  const prevY = prevYRef.current;

  const from = `0px ${totalLength === -1 ? 1 : totalLength}px`;
  const to = `${totalLength}px ${totalLength}px`;
  const transition = getTransitionVal(
    ['strokeDasharray'],
    animationDuration,
    typeof animationEasing === 'string' ? animationEasing : 'ease',
  );

  return (
    <JavascriptAnimate
      animationId={animationId}
      key={animationId}
      canBegin={totalLength > 0}
      duration={animationDuration}
      easing={animationEasing}
      isActive={isUpdateAnimationActive}
      begin={animationBegin}
    >
      {(animationElapsedTime: number) => {
        const currUpperWidth = interpolate(prevUpperWidth, upperWidth, animationElapsedTime);
        const currLowerWidth = interpolate(prevLowerWidth, lowerWidth, animationElapsedTime);
        const currHeight = interpolate(prevHeight, height, animationElapsedTime);
        const currX = interpolate(prevX, x, animationElapsedTime);
        const currY = interpolate(prevY, y, animationElapsedTime);

        if (pathRef.current) {
          prevUpperWidthRef.current = currUpperWidth;
          prevLowerWidthRef.current = currLowerWidth;
          prevHeightRef.current = currHeight;
          prevXRef.current = currX;
          prevYRef.current = currY;
        }
        const animationStyle =
          animationElapsedTime > 0 ? { transition, strokeDasharray: to } : { strokeDasharray: from };
        return (
          <path
            {...svgPropertiesAndEvents(trapezoidProps)}
            className={layerClass}
            d={getTrapezoidPath(currX, currY, currUpperWidth, currLowerWidth, currHeight)}
            ref={pathRef}
            style={{
              ...animationStyle,
              ...trapezoidProps.style,
            }}
          />
        );
      }}
    </JavascriptAnimate>
  );
};
